import React from 'react'
import { Navigate } from 'react-router-dom'
import { db, auth } from '../firebase-config'
import { setDoc, doc } from 'firebase/firestore'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import { useFormik } from 'formik'
import { object, string, ref } from 'yup'
import { CATEGORIES } from '../Tags'
import { Category } from '../components/Category'
import { Textarea } from '../components/Textarea'
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { Error } from '../components/Error'
import styles from './UserSignUp.module.css'
import categoryStyles from '../components/Category.module.css'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import mapboxgl from '!mapbox-gl' // eslint-disable-line import/no-webpack-loader-syntax

const requiredMsg = 'Dieses Feld ist verpflichtend'

const profileSchema = object({
  shopname: string().min(3, 'mindestens 3 Zeichen').required(requiredMsg),
  email: string().email('muss eine gültige E-Mail sein').required(requiredMsg),
  password: string().min(7, 'mindestens 7 Zeichen').required(requiredMsg),
  passwordConfirmation: string()
    .oneOf([ref('password'), null], 'Passwort stimmt nicht überein')
    .required(requiredMsg),
  street: string().min(5, 'mindestens 5 Zeichen').required(requiredMsg),
  postcode: string().min(4, 'mindestens 4 Zeichen').required(requiredMsg),
  place: string().min(2, 'mindestens 2 Zeichen').required(requiredMsg),
  openingHours: string().min(2, 'mindestens 2 Zeichen').required(requiredMsg),
  phone: string().min(4, 'mindestens 4 Zeichen').nullable(),
  link: string().url().nullable(),
})

const initialValues = {
  shopname: '',
  email: '',
  password: '',
  passwordConfirmation: '',
  street: '',
  postcode: '',
  place: '',
  openingHours: '',
  phone: '',
  link: '',
  categories: [],
}

async function getGeo(streetname, postcode, place) {
  const searchText = `${streetname}%20${postcode}%20${place}`
  const data = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${searchText}.json?access_token=${mapboxgl.accessToken}`,
  )
  const parsedData = await data.json()
  return parsedData.features[0].center
}

async function handleSubmit(values) {
  try {
    const userCredentials = await createUserWithEmailAndPassword(
      auth,
      values.email,
      values.password,
    )

    const geovalues = getGeo(values.street, values.postcode, values.place)

    const uid = userCredentials.user.uid
    try {
      await setDoc(doc(db, 'users', uid), {
        shopname: values.shopname,
        email: values.email,
        street: values.street,
        postcode: values.postcode,
        place: values.place,
        openingHours: values.openingHours,
        phone: values.phone,
        link: values.link,
        categories: values.categories,
        geocoordinates: await geovalues,
      })
    } catch (error) {
      toast.error(
        'Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        {
          position: 'top-center',
        },
      )
    }
  } catch (error) {
    error.code === 'auth/email-already-in-use'
      ? toast.error(
          'Die angegebene E-Mail ist schon in Verwendung. Bitte versuchen Sie es mit einer anderen.',
          {
            position: 'top-center',
          },
        )
      : toast.error(
          'Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
          {
            position: 'top-center',
          },
        )
  }
}

export const UserSignUp = ({ user, onSubmit = handleSubmit }) => {
  const formik = useFormik({
    initialValues,
    validationSchema: profileSchema,
    onSubmit: (values) => {
      onSubmit(values)
    },
  })

  const selectedCategories = formik.values.categories

  if (user) return <Navigate to="/#myshop"></Navigate>

  return (
    <>
      <form className={styles.usersignup} onSubmit={formik.handleSubmit}>
        <div>
          <Input
            name='shopname'
            type='text'
            label='Shop Name'
            placeholder='Mustershop'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.shopname}
          />
          {formik.touched.shopname && formik.errors.shopname && (
            <Error msg={formik.errors.shopname} />
          )}
        </div>
        <div>
          <Input
            name='email'
            type='email'
            label='E-Mail'
            placeholder='mustershop@email.com'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            required={true}
          />
          {formik.touched.email && formik.errors.email && (
            <Error msg={formik.errors.email} />
          )}
        </div>
        <div>
          <Input
            name='password'
            type='password'
            label='Passwort'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.password}
          />
          {formik.touched.password && formik.errors.password && (
            <Error msg={formik.errors.password} />
          )}
        </div>
        <div>
          <Input
            name='passwordConfirmation'
            type='password'
            label='Passwort wiederholen'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.passwordConfirmation}
          />
          {formik.touched.passwordConfirmation &&
            formik.errors.passwordConfirmation && (
              <Error msg={formik.errors.passwordConfirmation} />
            )}
        </div>
        <div>
          <Input
            name='street'
            type='text'
            label='Straße Hausnummer'
            placeholder='Musterstraße 12'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.street}
          />
          {formik.touched.street && formik.errors.street && (
            <Error msg={formik.errors.street} />
          )}
        </div>
        <div>
          <Input
            name='postcode'
            type='text'
            label='Postleitzahl'
            placeholder='5020'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.postcode}
          />
          {formik.touched.postcode && formik.errors.postcode && (
            <Error msg={formik.errors.postcode} />
          )}
        </div>
        <div>
          <Input
            name='place'
            type='text'
            label='Ort'
            placeholder='Salzburg'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.place}
          />
          {formik.touched.place && formik.errors.place && (
            <Error msg={formik.errors.place} />
          )}
        </div>
        <div>
          <Textarea
            name='openingHours'
            type='text'
            label='Öffnungszeiten'
            placeholder='von Montag bis Freitag,\nje von 12.30 - 17.45 Uhr'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.openingHours}
          />
          {formik.touched.openingHours && formik.errors.openingHours && (
            <Error msg={formik.errors.openingHours} />
          )}
        </div>
        <div>
          <Input
            name='phone'
            type='text'
            label='Telefonnummer (optional)'
            placeholder='0650/232432434'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.phone}
          />
          {formik.touched.phone && formik.errors.phone && (
            <Error msg={formik.errors.phone} />
          )}
        </div>
        <div>
          <Input
            name='link'
            type='text'
            label='Link (optional)'
            placeholder='https://MeineWebsite.at'
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.link}
          />
          {formik.touched && formik.errors.link && (
            <Error msg={formik.errors.link} />
          )}
        </div>

        <p>Ausgewählte Kategorien werden farblich markiert</p>
        <div className={categoryStyles.category_container}>
          {CATEGORIES.map((category) => {
            return (
              <Category
                key={category.id}
                category={category}
                isSelected={selectedCategories.includes(category.id)}
                onCategoryClick={() => {
                  const currentCategory = selectedCategories.includes(
                    category.id,
                  )

                  if (currentCategory) {
                    formik.setFieldValue(
                      'categories',
                      selectedCategories.filter((id) => id !== category.id),
                    )
                  } else {
                    formik.setFieldValue('categories', [
                      ...selectedCategories,
                      category.id,
                    ])
                  }
                }}
              />
            )
          })}
        </div>
        <Button type='submit'>Registrieren</Button>
      </form>
    </>
  )
}
